import React from 'react';
import { Box, CircularProgress, Typography, useTheme, Fade } from '@mui/material';
import { Smartphone, RocketLaunch, Code } from '@mui/icons-material';

const MobileEmulatorLoader = ({ message = 'Loading your testing environment...' }) => {
  const theme = useTheme();

  const deepBlue = {
    light: '#1976d2',
    dark: '#1565c0',
    subtle: theme.palette.mode === 'light' ? '#e3f2fd' : '#0d47a1'
  };

  return (
    <Fade in={true} timeout={800}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: 4,
          textAlign: 'center',
          mt: 20,
        }}>
        <Box
          sx={{
            position: 'relative',
            width: 120,
            height: 120,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <CircularProgress
            size={80}
            thickness={4}
            sx={{
              color: deepBlue.light,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': { opacity: 0.7 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.7 }
              }
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              animation: 'rotate 3s linear infinite',
              '@keyframes rotate': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}>
            <Smartphone
              sx={{
                fontSize: 24,
                color: deepBlue.dark,
                position: 'absolute',
                top: -35,
                left: '50%',
                transform: 'translateX(-50%)'
              }}
            />
          </Box>

          <Box
            sx={{
              position: 'absolute',
              animation: 'rotate 3s linear infinite reverse',
              '@keyframes rotateReverse': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(-360deg)' }
              }
            }}>
            <Code
              sx={{
                fontSize: 20,
                color: deepBlue.light,
                position: 'absolute',
                bottom: -30,
                left: '50%',
                transform: 'translateX(-50%)'
              }}
            />
          </Box>

          <RocketLaunch
            sx={{
              fontSize: 32,
              color: deepBlue.light,
              position: 'absolute',
              animation: 'bounce 1.5s ease-in-out infinite',
              '@keyframes bounce': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-5px)' }
              }
            }}
          />
        </Box>

        <Typography
          variant="h6"
          component="div"
          gutterBottom
          sx={{
            fontWeight: 600,
            color: theme.palette.mode === 'light' ? deepBlue.dark : theme.palette.primary.light,
            mb: 2
          }}>
          {message}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mt: 2
          }}>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: deepBlue.light,
                animation: `pulse-dots 1.4s ease-in-out ${index * 0.16}s infinite both`,
                '@keyframes pulse-dots': {
                  '0%, 80%, 100%': {
                    transform: 'scale(0.8)',
                    opacity: 0.5
                  },
                  '40%': {
                    transform: 'scale(1.2)',
                    opacity: 1
                  }
                }
              }}
            />
          ))}
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 3,
            maxWidth: 400,
            opacity: 0.8
          }}>
          Preparing your mobile testing environment...
        </Typography>
      </Box>
    </Fade>
  );
};

export default MobileEmulatorLoader;
